import React from 'react';
import {Divider, ScrollView, Typography} from '@genestack/ui';
import {Preloader} from '../../../../../components/preloader/preloader';
import {SidebarResultsHeader} from '../../../common/search-results-header';
import {extractInfiniteQueryItems} from '../../../../../components/pagination/infinite-query-items';
import {DocumentLink} from '../document-link';
import {PaginatedList} from '../../../../../components/pagination/paginated-list';
import {
    CategorySuggestion,
    DocNameSearchData,
    MetadataSuggestion
} from '../../../common/sidebar-search-field/interface';
import {
    isSearchByDocName,
    isSearchByMetadata,
    isSearchCategory
} from '../../../common/sidebar-search-field/suggestion-helpers';
import {useSearchDocsQuery} from './hooks/use-edit-mode-search-query-cache';
import {useDocumentSidebarState} from '../../../common/right-sidebar/hooks/use-document-sidebar-state';
import {EditRightPanelTab} from '../../../../../components/layout/page-layout/layout-typings';

const PAGE_SIZE = 60;

interface Props {
    searchData: DocNameSearchData | CategorySuggestion | MetadataSuggestion;
    rightSidebarProps: ReturnType<typeof useDocumentSidebarState>;
    documentId?: string;
}

export function FlatSearchResult(props: Props) {
    const {documentId} = props;

    const searchQuery = useSearchDocsQuery({
        pageSize: PAGE_SIZE,
        searchText: isSearchByDocName(props.searchData) ? props.searchData.searchString : undefined,
        categoryId: isSearchCategory(props.searchData) ? props.searchData.categoryId : undefined,
        metadata: isSearchByMetadata(props.searchData)
            ? [
                  {
                      fieldType: props.searchData.fieldType,
                      value: props.searchData.value
                  }
              ]
            : []
    });

    const items = extractInfiniteQueryItems(
        searchQuery,
        (response: PageResponse<EditModeDoc>) => response.data
    );

    const additionalDocClickHandler = React.useCallback(() => {
        if (isSearchByMetadata(props.searchData)) {
            props.rightSidebarProps.openSidebar();
            props.rightSidebarProps.setSelectedTab(EditRightPanelTab.METADATA);
        }
    }, [props]);

    const renderDocumentLink = React.useCallback(
        (doc: EditModeDoc) => {
            const element = (
                <DocumentLink
                    doc={doc}
                    active={doc.accession === documentId}
                    statusIconSettings={{showReadOnly: true, showDeleted: false}}
                    additionalClickHandler={additionalDocClickHandler}
                />
            );

            return [doc.accession, element] as [React.Key, React.ReactElement];
        },
        [documentId, additionalDocClickHandler]
    );

    const totalDocumentsNumber = searchQuery.data?.pages[0]?.total;

    return (
        <Preloader show={searchQuery.isLoading}>
            {items && (
                <React.Fragment>
                    <SidebarResultsHeader
                        isLoading={searchQuery.isFetchingNextPage || searchQuery.isLoading}
                    >
                        <Typography variant="section">
                            {totalDocumentsNumber
                                ? `Found ${searchQuery.data?.pages[0]?.total} documents`
                                : 'No results found'}
                        </Typography>
                    </SidebarResultsHeader>

                    <ScrollView>
                        <PaginatedList
                            pageSize={PAGE_SIZE}
                            items={items}
                            isLoadingNextPage={searchQuery.isFetchingNextPage}
                            hasNextPage={searchQuery.hasNextPage}
                            onLoadNext={searchQuery.fetchNextPage}
                            renderItem={renderDocumentLink}
                        />

                        <Divider variant="transparent" gap={4} />
                    </ScrollView>
                </React.Fragment>
            )}
        </Preloader>
    );
}
